/**
 * Helpdesk Analytics Page
 * Comprehensive helpdesk reporting dashboard
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import {
  Ticket,
  Clock,
  ShieldCheck,
  Star,
  Users,
  TrendingUp,
  Radio,
} from 'lucide-react';

interface HelpdeskSummary {
  totalTickets: number;
  openTickets: number;
  resolvedTickets: number;
  avgResponseTimeMinutes: number;
  avgResolutionTimeMinutes: number;
  slaCompliancePct: number;
  csatAverage: number;
  csatResponseRate: number;
}

interface AgentPerformance {
  agentName: string;
  assignedCount: number;
  resolvedCount: number;
  avgResponseTimeMinutes: number;
  avgResolutionTimeMinutes: number;
  slaCompliancePct: number;
  csatAverage: number;
}

interface SlaPriority {
  priority: string;
  totalTickets: number;
  breached: number;
  compliancePct: number;
  avgResponseMinutes: number;
  avgResolutionMinutes: number;
}

interface ChannelData {
  channel: string;
  count: number;
  percentage: number;
}

interface TrendData {
  period: string;
  created: number;
  resolved: number;
  closed: number;
}

interface Application {
  id: string;
  name: string;
}

const channelColors: Record<string, string> = {
  email: 'bg-blue-500',
  phone: 'bg-green-500',
  chat: 'bg-purple-500',
  widget: 'bg-orange-500',
  portal: 'bg-cyan-500',
  api: 'bg-gray-500',
};

function formatTime(minutes: number): string {
  if (minutes == null || isNaN(minutes)) return '-';
  if (minutes < 60) return `${Math.round(minutes)}m`;
  const hours = Math.floor(minutes / 60);
  const mins = Math.round(minutes % 60);
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
}

export function HelpdeskAnalyticsPage() {
  const [period, setPeriod] = useState('30d');
  const [applicationId, setApplicationId] = useState('');

  const queryParams = {
    period,
    applicationId: applicationId || undefined,
  };

  // Fetch applications
  const { data: appsData } = useQuery({
    queryKey: ['finance-applications'],
    queryFn: async () => {
      const result = await api.getFinanceApplications();
      return result;
    },
  });

  const applications = (appsData?.data || []) as Application[];

  // Fetch summary
  const { data: summaryData, isLoading: summaryLoading } = useQuery({
    queryKey: ['helpdesk-summary', queryParams],
    queryFn: () => api.getHelpdeskSummary(queryParams),
  });

  const summary = summaryData?.data as HelpdeskSummary | undefined;

  // Fetch agent performance
  const { data: agentData } = useQuery({
    queryKey: ['agent-performance', queryParams],
    queryFn: () => api.getAgentPerformance(queryParams),
  });

  const agents = (agentData?.data || []) as AgentPerformance[];

  // Fetch SLA compliance
  const { data: slaData } = useQuery({
    queryKey: ['sla-compliance', queryParams],
    queryFn: () => api.getSlaCompliance(queryParams),
  });

  const slaPriorities = (slaData?.data || []) as SlaPriority[];

  // Fetch channel breakdown
  const { data: channelData } = useQuery({
    queryKey: ['channel-breakdown', { period }],
    queryFn: () => api.getChannelBreakdown({ period }),
  });

  const channels = (channelData?.data || []) as ChannelData[];

  // Fetch ticket trends
  const { data: trendsData } = useQuery({
    queryKey: ['ticket-trends', queryParams],
    queryFn: () =>
      api.getTicketTrends({
        period,
        granularity: 'day',
        applicationId: applicationId || undefined,
      }),
  });

  const trends = (trendsData?.data || []) as TrendData[];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Helpdesk Analytics</h1>
          <p className="text-muted-foreground">
            Comprehensive reporting and performance metrics
          </p>
        </div>
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap gap-4">
        <div className="flex gap-1.5">
          {[
            { value: '7d', label: '7 Days' },
            { value: '30d', label: '30 Days' },
            { value: '90d', label: '90 Days' },
            { value: '365d', label: '1 Year' },
            { value: 'all', label: 'All Time' },
          ].map((p) => (
            <Button
              key={p.value}
              variant={period === p.value ? 'secondary' : 'outline'}
              size="sm"
              onClick={() => setPeriod(p.value)}
            >
              {p.label}
            </Button>
          ))}
        </div>

        <div className="w-64">
          <Select
            value={applicationId}
            onValueChange={(v) => setApplicationId(v === 'all' ? '' : v)}
          >
            <SelectTrigger>
              <SelectValue placeholder="All Applications" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Applications</SelectItem>
              {applications.map((app) => (
                <SelectItem key={app.id} value={app.id}>
                  {app.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Summary stat cards */}
      {summaryLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      ) : summary ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <Ticket className="h-4 w-4 text-blue-600" />
                <p className="text-sm text-muted-foreground">Total Tickets</p>
              </div>
              <p className="text-2xl font-bold mt-1">{summary.totalTickets}</p>
              <p className="text-xs text-muted-foreground">
                {summary.openTickets} open
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-orange-600" />
                <p className="text-sm text-muted-foreground">Avg Response Time</p>
              </div>
              <p className="text-2xl font-bold mt-1">
                {formatTime(summary.avgResponseTimeMinutes)}
              </p>
              <p className="text-xs text-muted-foreground">
                Resolution: {formatTime(summary.avgResolutionTimeMinutes)}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-green-600" />
                <p className="text-sm text-muted-foreground">SLA Compliance</p>
              </div>
              <p
                className={cn(
                  'text-2xl font-bold mt-1',
                  summary.slaCompliancePct >= 95
                    ? 'text-green-600'
                    : summary.slaCompliancePct >= 80
                      ? 'text-yellow-600'
                      : 'text-red-600'
                )}
              >
                {summary.slaCompliancePct != null
                  ? `${summary.slaCompliancePct.toFixed(1)}%`
                  : '-'}
              </p>
              <p className="text-xs text-muted-foreground">
                {summary.resolvedTickets} resolved
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <Star className="h-4 w-4 text-yellow-500" />
                <p className="text-sm text-muted-foreground">CSAT Average</p>
              </div>
              <p className="text-2xl font-bold mt-1">
                {summary.csatAverage != null
                  ? `${summary.csatAverage.toFixed(1)} / 5`
                  : '-'}
              </p>
              <p className="text-xs text-muted-foreground">
                {summary.csatResponseRate != null
                  ? `${summary.csatResponseRate.toFixed(0)}% response rate`
                  : 'No responses'}
              </p>
            </CardContent>
          </Card>
        </div>
      ) : null}

      {/* Second row: Agent Performance + SLA by Priority */}
      <div className="grid gap-4 lg:grid-cols-2">
        {/* Agent Performance */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Users className="h-4 w-4" />
              Agent Performance
            </CardTitle>
          </CardHeader>
          <CardContent>
            {agents.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                No agent data available
              </p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Agent</TableHead>
                      <TableHead className="text-right">Assigned</TableHead>
                      <TableHead className="text-right">Resolved</TableHead>
                      <TableHead className="text-right">Avg Response</TableHead>
                      <TableHead className="text-right">Avg Resolution</TableHead>
                      <TableHead className="text-right">SLA %</TableHead>
                      <TableHead className="text-right">CSAT</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {agents.map((agent) => (
                      <TableRow key={agent.agentName}>
                        <TableCell className="font-medium">{agent.agentName}</TableCell>
                        <TableCell className="text-right">{agent.assignedCount}</TableCell>
                        <TableCell className="text-right">{agent.resolvedCount}</TableCell>
                        <TableCell className="text-right">
                          {formatTime(agent.avgResponseTimeMinutes)}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatTime(agent.avgResolutionTimeMinutes)}
                        </TableCell>
                        <TableCell className="text-right">
                          <span
                            className={cn(
                              agent.slaCompliancePct >= 95
                                ? 'text-green-600'
                                : agent.slaCompliancePct >= 80
                                  ? 'text-yellow-600'
                                  : 'text-red-600'
                            )}
                          >
                            {agent.slaCompliancePct != null
                              ? `${agent.slaCompliancePct.toFixed(0)}%`
                              : '-'}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          {agent.csatAverage != null
                            ? agent.csatAverage.toFixed(1)
                            : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* SLA Compliance by Priority */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <ShieldCheck className="h-4 w-4" />
              SLA Compliance by Priority
            </CardTitle>
          </CardHeader>
          <CardContent>
            {slaPriorities.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                No SLA data available
              </p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Priority</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                      <TableHead className="text-right">Breached</TableHead>
                      <TableHead className="text-right">Compliance</TableHead>
                      <TableHead className="text-right">Avg Response</TableHead>
                      <TableHead className="text-right">Avg Resolution</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {slaPriorities.map((sla) => (
                      <TableRow key={sla.priority}>
                        <TableCell className="font-medium capitalize">
                          {sla.priority}
                        </TableCell>
                        <TableCell className="text-right">{sla.totalTickets}</TableCell>
                        <TableCell className="text-right">
                          <span className={sla.breached > 0 ? 'text-red-600 font-medium' : ''}>
                            {sla.breached}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <span
                            className={cn(
                              sla.compliancePct >= 95
                                ? 'text-green-600'
                                : sla.compliancePct >= 80
                                  ? 'text-yellow-600'
                                  : 'text-red-600'
                            )}
                          >
                            {sla.compliancePct != null
                              ? `${sla.compliancePct.toFixed(0)}%`
                              : '-'}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          {formatTime(sla.avgResponseMinutes)}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatTime(sla.avgResolutionMinutes)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Third row: Channel Breakdown + Ticket Trends */}
      <div className="grid gap-4 lg:grid-cols-2">
        {/* Channel Breakdown */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Radio className="h-4 w-4" />
              Channel Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent>
            {channels.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                No channel data available
              </p>
            ) : (
              <div className="space-y-3">
                {channels.map((ch) => (
                  <div key={ch.channel} className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium capitalize">{ch.channel}</span>
                      <span className="text-muted-foreground">
                        {ch.count} ({ch.percentage != null ? `${ch.percentage.toFixed(1)}%` : '0%'})
                      </span>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <div
                        className={cn(
                          'h-full rounded-full transition-all',
                          channelColors[ch.channel] || 'bg-primary'
                        )}
                        style={{ width: `${ch.percentage || 0}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Ticket Trends */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <TrendingUp className="h-4 w-4" />
              Ticket Trends
            </CardTitle>
          </CardHeader>
          <CardContent>
            {trends.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                No trend data available
              </p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Period</TableHead>
                      <TableHead className="text-right">Created</TableHead>
                      <TableHead className="text-right">Resolved</TableHead>
                      <TableHead className="text-right">Closed</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {trends.map((trend, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="font-medium text-sm">
                          {trend.period}
                        </TableCell>
                        <TableCell className="text-right">{trend.created}</TableCell>
                        <TableCell className="text-right">{trend.resolved}</TableCell>
                        <TableCell className="text-right">{trend.closed}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
